// src/components/awards/AwardFormModal.js
import React, { memo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { Close, ArrowForward, ArrowBack } from "@mui/icons-material";

const displayValue = (value) =>
  value !== null && value !== undefined && value !== "" ? value : "";

const FieldRow = ({ label, value, fullWidth = false }) => (
  <Grid container alignItems="center" spacing={1} sx={{ mb: 1 }}>
    <Grid item sx={{ width: fullWidth ? "100%" : 200 }}>
      <Typography variant="subtitle2">{label}</Typography>
    </Grid>
    {!fullWidth && (
      <Grid item xs>
        <Typography variant="body1">{displayValue(value)}</Typography>
      </Grid>
    )}
    {fullWidth && (
      <Grid item xs={12}>
        <Typography variant="body1">{displayValue(value)}</Typography>
      </Grid>
    )}
  </Grid>
);

const AwardFormModal = ({
  open,
  onClose,
  awardData,
  onPrevious,
  onNext,
  disablePrevious,
  disableNext,
}) => {
  if (!awardData) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth>
        <DialogTitle>Loading Award Details...</DialogTitle>
        <DialogContent>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      PaperProps={{
        sx: { width: "95vw", maxWidth: "90vw", height: "75vh" },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          component="div"
          variant="h5"
          textAlign="center"
          gutterBottom
        >
          Award Details
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: (theme) => theme.palette.grey[500] }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-around",
          px: 2,
          mb: 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={onPrevious}
          disabled={disablePrevious}
          startIcon={<ArrowBack />}
        >
          Previous
        </Button>
        <Button
          variant="outlined"
          onClick={onNext}
          disabled={disableNext}
          endIcon={<ArrowForward />}
        >
          Next
        </Button>
      </Box>
      <DialogContent dividers>
        <Grid container spacing={2}>
          {(awardData?.scholarship_name || awardData?.name) && (
            <Grid item xs={12}>
              <FieldRow
                label="Award Name:"
                value={awardData?.scholarship_name || awardData?.name}
              />
            </Grid>
          )}
          {awardData?.description && (
            <Grid item xs={12}>
              {/* Description takes full width */}
              <FieldRow label="Description:" value={awardData?.description} />
            </Grid>
          )}
          {awardData?.requirements && (
            <Grid item xs={12}>
              <FieldRow label="Requirements:" value={awardData?.requirements} />
            </Grid>
          )}
          {awardData?.award_amount && (
            <Grid item xs={12}>
              <FieldRow
                label="Amount:"
                value={
                  awardData?.award_amount ? `$${awardData?.award_amount}` : ""
                }
              />
            </Grid>
          )}
          {awardData?.specific_subject && (
            <Grid item xs={12}>
              <FieldRow
                label="Specific Subject:"
                value={awardData?.specific_subject}
              />
            </Grid>
          )}
          {awardData?.field_of_study && (
            <Grid item xs={12}>
              <FieldRow
                label="Field of Study:"
                value={awardData?.field_of_study}
              />
            </Grid>
          )}
          {awardData?.gender_specific && (
            <Grid item xs={12}>
              <FieldRow
                label="Gender Specific:"
                value={awardData?.gender_specific}
              />
            </Grid>
          )}
          {awardData?.number_of_awards && (
            <Grid item xs={12}>
              <FieldRow
                label="Number of Awards:"
                value={awardData?.number_of_awards}
              />
            </Grid>
          )}
          {awardData?.selected_by_name && (
            <Grid item xs={12}>
              <FieldRow
                label="Award Selected By:"
                value={awardData?.selected_by_name}
              />
            </Grid>
          )}
          {awardData?.presented_at_name && (
            <Grid item xs={12}>
              <FieldRow
                label="School Presented At:"
                value={awardData?.presented_at_name}
              />
            </Grid>
          )}
          {awardData?.additional_info_needed && (
            <Grid item xs={12}>
              <FieldRow
                label="Additional Info Needed?"
                value={awardData?.additional_info_needed ? "Yes" : "No"}
              />
            </Grid>
          )}
          {awardData?.city_name && (
            <Grid item xs={12}>
              <FieldRow label="City:" value={awardData?.city_name} />
            </Grid>
          )}
          {awardData?.claim_period && (
            <Grid item xs={12}>
              <FieldRow
                label="Claim Period (Years):"
                value={awardData?.claim_period}
              />
            </Grid>
          )}
          {awardData?.affiliations && (
            <Grid item xs={12}>
              <FieldRow label="Affiliations:" value={awardData?.affiliations} />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "0 1rem",
          p: "0.5rem",
          m: "0",
        }}
      >
        <Button onClick={onClose} color="inherit" startIcon={<Close />}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(AwardFormModal);
